import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BarChart3, Building2, Calculator, Check, CheckCircle2, Clock, Coins, FileCheck, FileText, Handshake, Home as HomeIcon, Map, MapPin, ShieldCheck, UserRound, Users, Zap } from "lucide-react";
import HeroIntakeForm from "../components/HeroIntakeForm";
import { Eyebrow, ArrowCta, Reveal, MaskLine, MaskLineView } from "../components/ui-bits";
import { useLang } from "../i18n/LangContext";

const HERO_BG = "/hero-builders.jpg";

const COPY = {
  en: {
    eyebrow: "Development Finance · Portugal 🇵🇹",
    headline: "Fast and easy developer financing.",
    heroBody:
      "We combine intelligent technology, local expertise and flexible capital to help experienced developers move projects forward — from land acquisition and licensing to bank financing.",
    stats: [
      { icon: Coins, t: "€1–5M", l: "Typical facility" },
      { icon: ShieldCheck, t: "Senior secured", l: "Asset-backed lending" },
      { icon: Clock, t: "Short-term", l: "6–24 months, bridge to bank financing" },
      { icon: MapPin, t: "Portugal", l: "Local underwriting and market expertise" },
    ],
    devEyebrow: "Development financing",
    devHeadline1: "From land to",
    devHeadline2: "construction readiness.",
    devCopy:
      "From land acquisition and licensing to presales, we provide short-term financing that helps experienced developers progress toward construction and traditional bank financing.",
    stages: [
      { id: "acquire", icon: Map, img: "/stage-acquire.jpg", t: "Acquire", d: "Secure land and development opportunities." },
      { id: "license", icon: HomeIcon, img: "/stage-unlock.jpg", t: "License", d: "Finance planning and licensing milestones." },
      { id: "bridge", icon: Building2, img: "/stage-bridge.jpg", t: "Bridge", d: "Bridge the gap to presales and bank financing." },
    ],
    panelEyebrow: "Built around developers",
    panelTitle1: "More than capital.",
    panelTitle2: "A developer's partner.",
    panelCopy:
      "We combine local market expertise with flexible capital to support your projects today and tomorrow.",
    processEyebrow: "A faster, clearer process",
    processHeadline: ["From", "application", "to indicative", "terms."],
    processCopy:
      "Submit your project once. We combine your information with market data and our expert underwriting to give you indicative terms within days.",
    processCta: "Get indicative terms",
    processNote: "No obligation. All financing subject to review and approval.",
    badgeMarket: "Market insights",
    badgeExpert: "Expert review",
    termsMock: { amount: "€2,000,000", sub: "12 months | Senior secured" },
    processTrust: [
      { icon: Zap, t: "Within days", s: "Indicative terms" },
      { icon: Users, t: "Expert-led", s: "Local market expertise" },
      { icon: ShieldCheck, t: "Your data is secure", s: "Confidential & protected" },
    ],
    brokerHeadline1: "Introduce a developer.",
    brokerHeadline2: "Earn a commission.",
    processSteps: [
      { n: "01", t: "Submit", d: "Tell us about your opportunity." },
      { n: "02", t: "Analyse", d: "We combine your information with market data and comparable transactions." },
      { n: "03", t: "Underwrite", d: "Our expert network reviews the opportunity alongside our credit team." },
      { n: "04", t: "Terms", d: "Receive indicative terms within days." },
      { n: "05", t: "Fund", d: "Complete due diligence, documentation and closing." },
    ],
    uwEyebrow: "Our underwriting",
    uwHeadline1: "Smarter underwriting.",
    uwHeadline2: "Faster decisions.",
    uwCopy:
      "We combine AI technology, an experienced credit team and a local expert network to analyse each opportunity and provide indicative terms in days.",
    uwSteps: [
      { icon: FileText, t: "AI analysis", d: "We analyse project data, documents, planning and market intelligence." },
      { icon: Users, t: "Expert review", d: "Our credit team and verified local experts assess structure, risk and terms." },
      { icon: CheckCircle2, t: "Indicative terms", d: "Receive clear terms in days, including facility amount, pricing and key conditions." },
    ],
    bannerEyebrow: "Verde expert network",
    bannerTitle: "Local expertise. Better decisions.",
    bannerCopy:
      "We work with a curated network of planning, legal, technical, valuation and market specialists across Portugal.",
    bannerCta: "Meet our expert network",
    bannerExperts: [
      { img: "/expert-sofia.jpg", name: "Sofia M.", role: "Urban planning & licensing expert" },
      { img: "/expert-carlos.jpg", name: "Carlos R.", role: "Technical due diligence expert" },
      { img: "/expert-marta.jpg", name: "Marta F.", role: "Valuation & market research expert" },
    ],
    brokerEyebrow: "For brokers and advisers",
    brokerCopy:
      "Connect qualifying residential development opportunities with Verde Capital and earn a commission when we fund the project.",
    brokerSteps: [
      { icon: FileText, t: "Submit", c: "Introduce a developer or opportunity through a simple process." },
      { icon: Users, t: "Assess", c: "Our team and expert network evaluate the opportunity." },
      { icon: Coins, t: "Earn", c: "Receive a commission when we fund the project." },
    ],
    brokerBenefits: [
      { icon: Handshake, t: "Attractive commissions", c: "For successful introductions." },
      { icon: ShieldCheck, t: "Work with a trusted lender", c: "Specialist in residential development finance." },
      { icon: UserRound, t: "Support from our expert network", c: "Planning, legal, technical and valuation specialists." },
    ],
    brokerCta: "Become a Verde partner",
  },
  pt: {
    eyebrow: "Financiamento ao Desenvolvimento · Portugal 🇵🇹",
    headline: "Financiamento rápido e simples para promotores.",
    heroBody:
      "Combinamos tecnologia inteligente, conhecimento local e capital flexível para ajudar promotores experientes a avançar com os seus projetos — da aquisição de terreno e do licenciamento ao financiamento bancário.",
    stats: [
      { icon: Coins, t: "€1–5M", l: "Montante típico" },
      { icon: ShieldCheck, t: "Garantia sénior", l: "Crédito garantido por ativos" },
      { icon: Clock, t: "Curto prazo", l: "6–24 meses, ponte até ao financiamento bancário" },
      { icon: MapPin, t: "Portugal", l: "Subscrição local e conhecimento do mercado" },
    ],
    devEyebrow: "Financiamento ao desenvolvimento",
    devHeadline1: "Do terreno à",
    devHeadline2: "prontidão para construção.",
    devCopy:
      "Da aquisição de terreno e do licenciamento até às pré-vendas: fornecemos financiamento de curto prazo que ajuda promotores experientes a avançar rumo à construção e ao financiamento bancário tradicional.",
    stages: [
      { id: "acquire", icon: Map, img: "/stage-acquire.jpg", t: "Adquirir", d: "Garantir terreno e oportunidades de desenvolvimento." },
      { id: "license", icon: HomeIcon, img: "/stage-unlock.jpg", t: "Licenciar", d: "Financiar marcos de planeamento e licenciamento." },
      { id: "bridge", icon: Building2, img: "/stage-bridge.jpg", t: "Fazer a ponte", d: "Colmatar a distância até às pré-vendas e ao financiamento bancário." },
    ],
    panelEyebrow: "Pensado para promotores",
    panelTitle1: "Mais do que capital.",
    panelTitle2: "O parceiro do promotor.",
    panelCopy:
      "Combinamos conhecimento local do mercado com capital para apoiar os seus projetos hoje e amanhã.",
    processEyebrow: "Um processo mais rápido e claro",
    processHeadline: ["Da", "candidatura", "às condições", "indicativas."],
    processCopy:
      "Apresente o seu projeto uma única vez. Combinamos a sua informação com dados de mercado e a nossa subscrição especializada para lhe dar condições indicativas em poucos dias.",
    processCta: "Obter condições indicativas",
    processNote: "Sem compromisso. Todo o financiamento está sujeito a análise e aprovação.",
    badgeMarket: "Informação de mercado",
    badgeExpert: "Revisão especializada",
    termsMock: { amount: "€2.000.000", sub: "12 meses | Garantia sénior" },
    processTrust: [
      { icon: Zap, t: "Em poucos dias", s: "Condições indicativas" },
      { icon: Users, t: "Especialistas", s: "Conhecimento local do mercado" },
      { icon: ShieldCheck, t: "Os seus dados estão seguros", s: "Confidencial e protegido" },
    ],
    processSteps: [
      { n: "01", t: "Apresentar", d: "Fale-nos da sua oportunidade." },
      { n: "02", t: "Analisar", d: "Combinamos a sua informação com dados de mercado e operações comparáveis." },
      { n: "03", t: "Subscrever", d: "A nossa rede de especialistas avalia a oportunidade com a equipa de crédito." },
      { n: "04", t: "Condições", d: "Receba condições indicativas em poucos dias." },
      { n: "05", t: "Financiar", d: "Conclua a due diligence, a documentação e o fecho." },
    ],
    uwEyebrow: "A nossa subscrição",
    uwHeadline1: "Subscrição mais inteligente.",
    uwHeadline2: "Decisões mais rápidas.",
    uwCopy:
      "Combinamos tecnologia de IA, uma equipa de crédito experiente e uma rede local de especialistas para analisar cada oportunidade e fornecer condições indicativas em poucos dias.",
    uwSteps: [
      { icon: FileText, t: "Análise por IA", d: "Analisamos dados do projeto, documentos, licenciamento e inteligência de mercado." },
      { icon: Users, t: "Revisão especializada", d: "A nossa equipa de crédito e especialistas locais verificados avaliam estrutura, risco e condições." },
      { icon: CheckCircle2, t: "Condições indicativas", d: "Receba condições claras em poucos dias, incluindo montante, preço e condições-chave." },
    ],
    bannerEyebrow: "Rede de especialistas Verde",
    bannerTitle: "Conhecimento local. Melhores decisões.",
    bannerCopy:
      "Trabalhamos com uma rede selecionada de especialistas em planeamento, legal, técnico, avaliação e mercado em todo o Portugal.",
    bannerCta: "Conheça a nossa rede de especialistas",
    bannerExperts: [
      { img: "/expert-sofia.jpg", name: "Sofia M.", role: "Especialista em urbanismo e licenciamento" },
      { img: "/expert-carlos.jpg", name: "Carlos R.", role: "Especialista em due diligence técnica" },
      { img: "/expert-marta.jpg", name: "Marta F.", role: "Especialista em avaliação e estudo de mercado" },
    ],
    brokerEyebrow: "Para intermediários e consultores",
    brokerHeadline1: "Apresente um promotor.",
    brokerHeadline2: "Ganhe uma comissão.",
    brokerCopy:
      "Ligue oportunidades qualificadas de desenvolvimento residencial à Verde Capital e ganhe uma comissão quando financiarmos o projeto.",
    brokerSteps: [
      { icon: FileText, t: "Apresentar", c: "Apresente um promotor ou oportunidade através de um processo simples." },
      { icon: Users, t: "Avaliar", c: "A nossa equipa e a rede de especialistas avaliam a oportunidade." },
      { icon: Coins, t: "Ganhar", c: "Receba uma comissão quando financiarmos o projeto." },
    ],
    brokerBenefits: [
      { icon: Handshake, t: "Comissões atractivas", c: "Por apresentações bem-sucedidas." },
      { icon: ShieldCheck, t: "Trabalhe com um credor de confiança", c: "Especialistas em financiamento ao desenvolvimento residencial." },
      { icon: UserRound, t: "Apoio da nossa rede de especialistas", c: "Especialistas em planeamento, legal, técnico e avaliação." },
    ],
    brokerCta: "Torne-se parceiro da Verde",
  },
  nl: {
    eyebrow: "Ontwikkelfinanciering · Portugal 🇵🇹",
    headline: "Snelle en eenvoudige financiering voor ontwikkelaars.",
    heroBody:
      "Wij combineren slimme technologie, lokale kennis en flexibel kapitaal om ervaren ontwikkelaars vooruit te helpen — van grondverwerving en vergunningen tot bankfinanciering.",
    stats: [
      { icon: Coins, t: "€1–5 mln", l: "Typisch krediet" },
      { icon: ShieldCheck, t: "Senior zekerheid", l: "Leningen gedekt op vastgoed" },
      { icon: Clock, t: "Kortlopend", l: "6–24 maanden, brug naar bankfinanciering" },
      { icon: MapPin, t: "Portugal", l: "Lokale beoordeling en marktkennis" },
    ],
    devEyebrow: "Ontwikkelfinanciering",
    devHeadline1: "Van grond tot",
    devHeadline2: "bouwgereedheid.",
    devCopy:
      "Van grondverwerving en vergunningen tot voorverkoop: wij bieden kortlopende financiering die ervaren ontwikkelaars helpt door te groeien naar de bouwfase en traditionele bankfinanciering.",
    stages: [
      { id: "acquire", icon: Map, img: "/stage-acquire.jpg", t: "Verwerven", d: "Zeker grond en ontwikkelkansen." },
      { id: "license", icon: HomeIcon, img: "/stage-unlock.jpg", t: "Vergunnen", d: "Financier bestemmingsplan- en vergunningsmijlpalen." },
      { id: "bridge", icon: Building2, img: "/stage-bridge.jpg", t: "Overbruggen", d: "Overbrug de afstand naar voorverkoop en bankfinanciering." },
    ],
    panelEyebrow: "Gebouwd voor ontwikkelaars",
    panelTitle1: "Meer dan kapitaal.",
    panelTitle2: "De partner van de ontwikkelaar.",
    panelCopy:
      "Wij combineren lokale marktkennis met flexibel kapitaal om uw projecten vandaag en morgen te ondersteunen.",
    processEyebrow: "Een sneller, duidelijker proces",
    processHeadline: ["Van", "aanvraag", "tot indicatieve", "voorwaarden."],
    processCopy:
      "Dien uw project één keer in. Wij combineren uw informatie met marktgegevens en onze deskundige beoordeling tot indicatieve voorwaarden binnen enkele dagen.",
    processCta: "Indicatieve voorwaarden aanvragen",
    processNote: "Geen verplichtingen. Alle financiering is onder voorbehoud van beoordeling en goedkeuring.",
    badgeMarket: "Marktinformatie",
    badgeExpert: "Expertcontrole",
    termsMock: { amount: "€2.000.000", sub: "12 maanden | Senior zekerheid" },
    processTrust: [
      { icon: Zap, t: "Binnen enkele dagen", s: "Indicatieve voorwaarden" },
      { icon: Users, t: "Expertgestuurd", s: "Lokale marktkennis" },
      { icon: ShieldCheck, t: "Uw gegevens zijn veilig", s: "Vertrouwelijk en beschermd" },
    ],
    processSteps: [
      { n: "01", t: "Indienen", d: "Vertel ons over uw kans." },
      { n: "02", t: "Analyseren", d: "Wij combineren uw informatie met marktgegevens en vergelijkbare transacties." },
      { n: "03", t: "Beoordelen", d: "Ons expertennetwerk toetst de kans samen met ons kredietteam." },
      { n: "04", t: "Voorwaarden", d: "Ontvang indicatieve voorwaarden binnen enkele dagen." },
      { n: "05", t: "Financieren", d: "Vereenvoudig due diligence, documentatie en afsluiting." },
    ],
    uwEyebrow: "Onze kredietbeoordeling",
    uwHeadline1: "Slimmere beoordeling.",
    uwHeadline2: "Snellere beslissingen.",
    uwCopy:
      "Wij combineren AI-technologie, een ervaren kredietteam en een lokaal expertennetwerk om elke kans te analyseren en binnen enkele dagen indicatieve voorwaarden te geven.",
    uwSteps: [
      { icon: FileText, t: "AI-analyse", d: "Wij analyseren projectgegevens, documenten, planning en marktinformatie." },
      { icon: Users, t: "Expertcontrole", d: "Ons kredietteam en geverifieerde lokale experts beoordelen structuur, risico en voorwaarden." },
      { icon: CheckCircle2, t: "Indicatieve voorwaarden", d: "Ontvang heldere voorwaarden binnen enkele dagen: kredietbedrag, prijs en kernvoorwaarden." },
    ],
    bannerEyebrow: "Verde expertennetwerk",
    bannerTitle: "Lokale kennis. Betere beslissingen.",
    bannerCopy:
      "Wij werken met een samengesteld netwerk van specialisten in ruimtelijke ordening, juridisch, techniek, taxatie en markt in heel Portugal.",
    bannerCta: "Ontmoet ons expertennetwerk",
    bannerExperts: [
      { img: "/expert-sofia.jpg", name: "Sofia M.", role: "Expert in ruimtelijke ordening en vergunningen" },
      { img: "/expert-carlos.jpg", name: "Carlos R.", role: "Expert in technische due diligence" },
      { img: "/expert-marta.jpg", name: "Marta F.", role: "Expert in taxatie en marktonderzoek" },
    ],
    brokerEyebrow: "Voor tussenpersonen en adviseurs",
    brokerHeadline1: "Breng een ontwikkelaar in.",
    brokerHeadline2: "Ontvang provisie.",
    brokerCopy:
      "Breng gekwalificeerde woningbouwontwikkelingen met Verde Capital in contact en ontvang provisie zodra wij het project financieren.",
    brokerSteps: [
      { icon: FileText, t: "Indienen", c: "Breng een ontwikkelaar of kans in via een eenvoudig proces." },
      { icon: Users, t: "Beoordelen", c: "Ons team en expertennetwerk toetsen de kans." },
      { icon: Coins, t: "Verdienen", c: "Ontvang provisie zodra wij het project financieren." },
    ],
    brokerBenefits: [
      { icon: Handshake, t: "Aantrekkelijke provisies", c: "Voor succesvolle introducties." },
      { icon: ShieldCheck, t: "Werk met een betrouwbare geldverstrekker", c: "Specialist in financiering van woningbouwontwikkeling." },
      { icon: UserRound, t: "Ondersteuning vanuit ons expertennetwerk", c: "Specialisten in ruimtelijke ordening, juridisch, techniek en taxatie." },
    ],
    brokerCta: "Word partner van Verde",
  },
};

function PhoneShell({ children, tilt = "" }) {
  return (
    <div className={`relative shrink-0 ${tilt}`}>
      <div className="overflow-hidden rounded-[48px] border-4 border-sand/80 bg-verde-950 p-3 shadow-2xl shadow-verde-950/30 ring-1 ring-black/10 transition-transform duration-300 hover:-translate-y-1">
        <div className="relative overflow-hidden rounded-[38px] border border-sand/40 bg-cream px-4 pb-4 pt-8">
          <div className="absolute left-1/2 top-3.5 z-30 h-4 w-24 -translate-x-1/2 rounded-full bg-verde-950" />
          {children}
        </div>
      </div>
    </div>
  );
}

function MenuGlyph() {
  return (
    <span className="space-y-[3px]" aria-hidden="true">
      <span className="block h-[2px] w-4 rounded bg-charcoal" />
      <span className="block h-[2px] w-4 rounded bg-charcoal" />
    </span>
  );
}

function ProjectPhone() {
  const { lang } = useLang();
  const T = {
    en: {
      project: "Project", name: "Cascais Residences", loan: "Loan amount",
      rows: [["Purpose", "Land acquisition"], ["Location", "Cascais, Portugal"]],
      status: "Status", badge: "Under review", cont: "Continue application",
      timeline: [
        ["Application submitted", "12 Mar 2025", "done"],
        ["Initial analysis", "In progress", "active"],
        ["Underwriting", "", "todo"],
        ["Indicative terms", "", "todo"],
        ["Closing", "", "todo"],
      ],
    },
    pt: {
      project: "Projeto", name: "Cascais Residences", loan: "Montante do crédito",
      rows: [["Finalidade", "Aquisição de terreno"], ["Localização", "Cascais, Portugal"]],
      status: "Estado", badge: "Em análise", cont: "Continuar candidatura",
      timeline: [
        ["Candidatura apresentada", "12 Mar 2025", "done"],
        ["Análise inicial", "Em curso", "active"],
        ["Subscrição", "", "todo"],
        ["Condições indicativas", "", "todo"],
        ["Fecho", "", "todo"],
      ],
    },
    nl: {
      project: "Project", name: "Cascais Residences", loan: "Kredietbedrag",
      rows: [["Doel", "Grondverwerving"], ["Locatie", "Cascais, Portugal"]],
      status: "Status", badge: "In beoordeling", cont: "Aanvraag voortzetten",
      timeline: [
        ["Aanvraag ingediend", "12 mrt 2025", "done"],
        ["Eerste analyse", "Lopend", "active"],
        ["Kredietbeoordeling", "", "todo"],
        ["Indicatieve voorwaarden", "", "todo"],
        ["Afsluiting", "", "todo"],
      ],
    },
  }[lang];
  return (
    <PhoneShell tilt="z-10 -rotate-[7deg] w-[218px] sm:w-[252px]">
      <div className="flex items-center justify-between">
        <img loading="lazy" decoding="async" src="/logo-official.png" alt="Verde Capital" className="h-5 w-auto" />
        <MenuGlyph />
      </div>
      <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.18em] text-fog">{T.project}</p>
      <p className="font-display text-[15px] font-bold text-charcoal">{T.name}</p>
      <img loading="lazy" decoding="async" src="/stage-coast.jpg" alt={T.name} className="mt-2.5 h-24 w-full rounded-xl border border-sand/60 object-cover" />
      <dl className="mt-3.5 space-y-[7px]">
        <div className="flex items-center justify-between">
          <dt className="text-[10px] text-fog">{T.loan}</dt>
          <dd className="font-mono text-[10px] font-semibold tracking-tight text-verde-950">€2,500,000</dd>
        </div>
        {T.rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-[10px]">
            <dt className="text-fog">{k}</dt>
            <dd className="font-medium text-charcoal">{v}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <dt className="text-[10px] text-fog">{T.status}</dt>
          <dd className="rounded-full border border-brass/30 bg-brass/15 px-2 py-0.5 font-mono text-[9px] text-brass">
            {T.badge}
          </dd>
        </div>
      </dl>
      <ul className="mt-4">
        {T.timeline.map(([t, s, state], i) => (
          <li key={t} className="relative flex gap-2.5 pb-3 last:pb-0">
            {i < T.timeline.length - 1 && <span className="absolute left-[7px] top-4 h-full w-px bg-sand" />}
            <span
              className={`relative z-10 mt-0.5 flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border-2 ${
                state === "done" ? "border-verde-900 bg-verde-900" : state === "active" ? "border-brass bg-cream" : "border-sand bg-cream"
              }`}
            >
              {state === "done" && <Check className="h-2 w-2 text-brass" strokeWidth={3.5} />}
              {state === "active" && <span className="h-1.5 w-1.5 rounded-full bg-brass" />}
            </span>
            <div>
              <p className="text-[10.5px] font-semibold leading-tight text-charcoal">{t}</p>
              {s && <p className="font-mono text-[9px] leading-tight text-fog">{s}</p>}
            </div>
          </li>
        ))}
      </ul>
      <span className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-verde-950 py-2.5 text-[11px] font-medium text-cream shadow-md transition-colors hover:bg-verde-800">
        {T.cont} <ArrowRight className="h-3 w-3" />
      </span>
    </PhoneShell>
  );
}

function ApplicationPhone() {
  const { lang } = useLang();
  const T = {
    en: {
      title: "Your application",
      cards: [
        { icon: Building2, t: "Project details", s: "Completed", state: "done" },
        { icon: Calculator, t: "Financial information", s: "Completed", state: "done" },
        { icon: FileCheck, t: "Planning & licensing", s: "In progress", state: "active" },
        { icon: FileText, t: "Supporting documents", s: "Not started", state: "todo" },
      ],
    },
    pt: {
      title: "A sua candidatura",
      cards: [
        { icon: Building2, t: "Detalhes do projeto", s: "Concluído", state: "done" },
        { icon: Calculator, t: "Informação financeira", s: "Concluído", state: "done" },
        { icon: FileCheck, t: "Planeamento e licenciamento", s: "Em curso", state: "active" },
        { icon: FileText, t: "Documentos de suporte", s: "Não iniciado", state: "todo" },
      ],
    },
    nl: {
      title: "Uw aanvraag",
      cards: [
        { icon: Building2, t: "Projectgegevens", s: "Voltooid", state: "done" },
        { icon: Calculator, t: "Financiële gegevens", s: "Voltooid", state: "done" },
        { icon: FileCheck, t: "Planning & vergunningen", s: "Lopend", state: "active" },
        { icon: FileText, t: "Ondersteunende documenten", s: "Niet gestart", state: "todo" },
      ],
    },
  }[lang];
  return (
    <PhoneShell tilt="rotate-[6deg] -mt-24 w-[204px] sm:mt-20 sm:w-[232px]">
      <div className="flex items-center justify-between">
        <img loading="lazy" decoding="async" src="/logo-official.png" alt="Verde Capital" className="h-5 w-auto" />
        <MenuGlyph />
      </div>
      <p className="mt-4 font-display text-[15px] font-bold text-charcoal">{T.title}</p>
      <div className="mt-3 space-y-2">
        {T.cards.map((c) => (
          <div
            key={c.t}
            className="flex items-center gap-2 rounded-xl border border-sand bg-cream-dark/70 p-2.5 backdrop-blur-sm transition-all duration-300 hover:border-brass/40 hover:shadow-md"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-verde-50 text-verde-700">
              <c.icon className="h-3 w-3" strokeWidth={1.8} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold leading-tight text-charcoal">{c.t}</p>
              <p className="text-[9px] leading-tight text-fog">{c.s}</p>
            </div>
            {c.state === "done" ? (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-verde-900">
                <Check className="h-2 w-2 text-brass" strokeWidth={3.5} />
              </span>
            ) : c.state === "active" ? (
              <span className="h-4 w-4 shrink-0 rounded-full border-2 border-sand border-t-brass" />
            ) : (
              <span className="h-4 w-4 shrink-0 rounded-full border-2 border-sand" />
            )}
          </div>
        ))}
      </div>
    </PhoneShell>
  );
}

function HeroBackground() {
  return (
    <img
      src="/hero-builders.jpg"
      alt="Developers reviewing blueprints at a coastal construction site in Portugal"
      className="absolute inset-0 h-full w-full object-cover"
      loading="eager"
      fetchpriority="high"
      data-testid="hero-photo"
    />
  );
}

function ProcessMobileStep({ s, i, isLast, c }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center", "end start"] });
  const cardBg = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], ["#FFFFFF", "#E4EFE8", "#E4EFE8", "#FFFFFF"]);
  const cardShadow = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], [
    "0 18px 40px -26px rgba(7,39,29,0.35)",
    "0 24px 48px -24px rgba(13,59,46,0.45)",
    "0 24px 48px -24px rgba(13,59,46,0.45)",
    "0 18px 40px -26px rgba(7,39,29,0.35)",
  ]);
  const ring = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], ["#E2DACD", "#0D3B2E", "#0D3B2E", "#E2DACD"]);
  const circleBg = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], ["#FFFFFF", "#0D3B2E", "#0D3B2E", "#FFFFFF"]);
  const circleText = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], ["#1F3A33", "#FFFFFF", "#FFFFFF", "#1F3A33"]);
  return (
    <li ref={ref} className="relative flex gap-4 pb-5 last:pb-0" data-testid={`process-mobile-step-${s.n}`}>
      {!isLast && <span className="absolute left-[21px] top-12 h-[calc(100%-3rem)] w-px bg-sand" aria-hidden="true" />}
      <motion.span
        style={{ backgroundColor: circleBg, color: circleText, borderColor: ring }}
        className="relative z-10 mt-3 flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full border font-mono text-[13px] font-semibold"
      >
        {s.n}
      </motion.span>
      <Reveal delay={0.05 + i * 0.05} className="min-w-0 flex-1">
        <motion.div style={{ backgroundColor: cardBg, boxShadow: cardShadow }} className="h-full rounded-2xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_10px_22px_-14px_rgba(7,39,29,0.4)]">
                <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                {i === 0 && <FileText className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />}
                {i === 1 && <BarChart3 className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />}
                {i === 2 && <ShieldCheck className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />}
                {i === 3 && <FileText className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />}
                {i === 4 && <CheckCircle2 className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />}
              </span>
              <div>
                <p className="font-display text-[17px] font-bold text-charcoal">{s.t}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-fog">{s.d}</p>
              </div>
            </div>
            {i === 0 && (
              <img loading="lazy" decoding="async" src="/stage-acquire.jpg" alt="" aria-hidden="true" className="h-[76px] w-[104px] shrink-0 rounded-xl object-cover" />
            )}
            {i === 1 && (
              <span className="relative h-[76px] w-[104px] shrink-0 overflow-hidden rounded-xl">
                <img loading="lazy" decoding="async" src="/stage-unlock.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-charcoal shadow">
                  <BarChart3 className="h-2.5 w-2.5 text-verde-700" /> {c.badgeMarket}
                </span>
              </span>
            )}
            {i === 2 && (
              <span className="flex h-[76px] w-[112px] shrink-0 flex-col items-center justify-center gap-1.5 rounded-xl bg-[#E8F0EC] px-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-verde-800 shadow-sm">
                  <CheckCircle2 className="h-2.5 w-2.5" /> {c.badgeExpert}
                </span>
                <span className="flex -space-x-2">
                  {["/team-miguel.jpg", "/team-tomas.jpg", "/team-mariana.jpg"].map((src) => (
                    <img loading="lazy" decoding="async" key={src} src={src} alt="" aria-hidden="true" className="h-7 w-7 rounded-full object-cover ring-2 ring-white" />
                  ))}
                </span>
              </span>
            )}
            {i === 3 && (
              <span className="flex h-[76px] w-[128px] shrink-0 flex-col items-center justify-center rounded-xl bg-[#E8F0EC] px-2 text-center">
                <span className="font-display text-lg font-black leading-none text-verde-800">{c.termsMock.amount}</span>
                <span className="mt-1 text-[10px] leading-tight text-fog">{c.termsMock.sub}</span>
              </span>
            )}
            {i === 4 && (
              <img loading="lazy" decoding="async" src="/stage-bridge.jpg" alt="" aria-hidden="true" className="h-[76px] w-[104px] shrink-0 rounded-xl object-cover" />
            )}
          </div>
        </motion.div>
      </Reveal>
    </li>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { lang, lp } = useLang();
  const c = COPY[lang];
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <div className="bg-cream">
      {/* ── HERO ─────────────────────────────────────────── */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -top-10 -bottom-16">
          <HeroBackground />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-cream/95 via-cream/80 to-cream/25 lg:from-cream/95 lg:via-cream/70 lg:to-cream/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-cream to-transparent" />

        <div className="container-x relative pt-28 sm:pt-36 lg:pt-40 pb-12 sm:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-10 items-center">
          <div className="lg:col-span-6 hero-halo">
            <Eyebrow>{c.eyebrow}</Eyebrow>
            <h1 className="display mt-6 text-[2.9rem] sm:text-6xl lg:text-[72px] text-charcoal" data-testid="hero-headline">
              <MaskLine delay={0.15}>{c.headline}</MaskLine>
            </h1>
            <Reveal delay={0.5} className="mt-7 max-w-lg">
              <p className="text-lg leading-relaxed text-ink">
                {c.heroBody}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 flex lg:justify-end">
            <HeroIntakeForm />
          </div>
        </div>

        <div className="relative border-y border-sand bg-white">
          <div className="container-x grid grid-cols-2 lg:grid-cols-4 lg:divide-x divide-sand" data-testid="hero-stats">
            {c.stats.map((s, i) => (
              <Reveal key={s.t} delay={0.08 + i * 0.08} className="py-6 lg:py-9">
                <div className={`flex items-start gap-3.5 ${i > 0 ? "lg:pl-10" : ""}`}>
                  <s.icon className="h-7 w-7 shrink-0 text-verde-800" strokeWidth={1.5} aria-hidden="true" />
                  <div>
                    <p className="font-display text-lg font-bold tracking-tight text-charcoal">{s.t}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-fog">{s.l}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEVELOPMENT FINANCING (SECTION TWO) ──────────── */}
      <section className="py-20 lg:py-28 bg-cream border-b border-sand">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14">
          <div className="lg:col-span-7">
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-verde-700">
              <span className="h-[2px] w-9 rounded bg-verde-700" aria-hidden="true" />
              {c.devEyebrow}
            </p>
            <h2 className="display mt-6 text-4xl leading-[1.02] text-charcoal sm:text-5xl" data-testid="development-financing-headline">
              <MaskLineView>{c.devHeadline1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.devHeadline2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-xl">
              <p className="text-[15px] leading-relaxed text-fog" data-testid="development-financing-copy">
                {c.devCopy}
              </p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3" data-testid="stage-cards">
              {c.stages.map((s, i) => (
                <Reveal key={s.id} delay={0.08 + i * 0.08}>
                  <div className="card h-full overflow-hidden" data-testid={`stage-card-${s.id}`}>
                    <div className="relative p-3 pb-0">
                      <img loading="lazy" decoding="async" src={s.img} alt={s.t} className="h-28 w-full rounded-xl object-cover" />
                      <span className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_12px_26px_-16px_rgba(7,39,29,0.45)]">
                        <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                        <s.icon className="relative h-5 w-5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                      </span>
                    </div>
                    <div className="px-5 pb-5 pt-8">
                      <p className="font-display text-lg font-bold text-charcoal">{s.t}</p>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-fog">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2} className="h-full">
              <div className="relative min-h-[440px] overflow-hidden rounded-3xl lg:min-h-full" data-testid="built-around-developers-panel">
                <img loading="lazy" decoding="async" src="/stage-coast.jpg" alt="Development on the Portuguese coast" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-x-5 bottom-5 rounded-2xl bg-verde-950/95 p-6 shadow-xl shadow-verde-950/30 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-[350px]">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mint">{c.panelEyebrow}</p>
                  <p className="mt-3 font-display text-2xl font-bold leading-snug text-white">
                    {c.panelTitle1}
                    <br />{c.panelTitle2}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-cream/75">
                    {c.panelCopy}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── A FASTER, CLEARER PROCESS ────────────────────── */}
      <section id="process" className="relative overflow-hidden bg-cream py-24 lg:py-36">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(232,240,236,0.6),rgba(242,236,225,0.28)_48%,transparent_78%)]"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.32]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(#E2DACD 1px, transparent 1px), linear-gradient(90deg, #E2DACD 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
        <div className="container-x relative grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start lg:gap-12">
          <div className="lg:col-span-4 lg:pt-10">
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-brass">
              <span className="h-px w-10 bg-brass" aria-hidden="true" />
              {c.processEyebrow}
            </p>
            <h2 className="display mt-7 text-4xl leading-[1.02] text-charcoal sm:text-5xl lg:text-[56px]" data-testid="process-headline">
              <MaskLineView>{c.processHeadline[0]}</MaskLineView>
              <MaskLineView delay={0.06}>{c.processHeadline[1]}</MaskLineView>
              <MaskLineView delay={0.12}>{c.processHeadline[2]}</MaskLineView>
              <MaskLineView delay={0.18}>{c.processHeadline[3]}</MaskLineView>
            </h2>
            <Reveal delay={0.25} className="mt-7 max-w-sm">
              <p className="text-[15px] leading-relaxed text-fog" data-testid="process-copy">
                {c.processCopy}
              </p>
            </Reveal>
            <Reveal delay={0.35} className="mt-9 hidden lg:block">
              <ArrowCta to={lp("/apply")} testid="process-cta">{c.processCta}</ArrowCta>
            </Reveal>
          </div>

          <div className="lg:col-span-8 grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-10 lg:items-center">
            <Reveal delay={0.15} className="hidden lg:block">
              <div className="relative flex items-start justify-center py-6" data-testid="phone-duo">
                <div
                  className="pointer-events-none absolute inset-x-8 bottom-2 h-16 rounded-full bg-verde-950/15 blur-2xl"
                  aria-hidden="true"
                />
                <div className="hidden flex-col items-center sm:flex-row sm:items-start sm:justify-center lg:flex">
                  <ProjectPhone />
                  <div className="hidden sm:block">
                    <ApplicationPhone />
                  </div>
                </div>
              </div>
            </Reveal>

            <ol className="relative mx-auto hidden max-w-[340px] lg:mx-0 lg:block lg:max-w-none">
              {c.processSteps.map((s, i) => (
                <Reveal key={s.n} delay={0.1 + i * 0.07}>
                  <li
                    className="relative flex gap-5 pb-9 transition-transform duration-300 last:pb-0 hover:-translate-y-0.5"
                    data-testid={`process-step-${s.n}`}
                  >
                    {i < c.processSteps.length - 1 && (
                      <span className="absolute left-[22px] top-12 h-[calc(100%-3rem)] w-px bg-sand" aria-hidden="true" />
                    )}
                    <span className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] font-mono text-[13px] font-semibold text-brass ring-1 ring-verde-800/10 shadow-[0_12px_26px_-16px_rgba(7,39,29,0.45)]">
                      <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                      <span className="relative">{s.n}</span>
                    </span>
                    <div className="pt-0.5">
                      <p className="font-display text-[17px] font-bold text-charcoal">{s.t}</p>
                      <p className="mt-1 text-[13px] leading-relaxed text-fog">{s.d}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>

            {/* MOBILE: card timeline with visuals + trust band */}
            <div className="lg:hidden" data-testid="process-mobile">
              <ol className="relative">
                {c.processSteps.map((s, i) => (
                  <ProcessMobileStep key={s.n} s={s} i={i} isLast={i === c.processSteps.length - 1} c={c} />
                ))}
              </ol>

              <div className="mt-7 grid grid-cols-3 divide-x divide-sand rounded-2xl bg-[#E8F0EC]/60 py-5" data-testid="process-trust">
                {c.processTrust.map((b) => (
                  <div key={b.t} className="flex flex-col items-center gap-1 px-2 text-center">
                    <b.icon className="h-5 w-5 text-verde-700" strokeWidth={1.7} aria-hidden="true" />
                    <p className="text-[12px] font-bold leading-tight text-charcoal">{b.t}</p>
                    <p className="text-[10px] leading-tight text-fog">{b.s}</p>
                  </div>
                ))}
              </div>

              <Reveal delay={0.1} className="mt-9 flex flex-col items-center gap-3 text-center">
                <ArrowCta to={lp("/apply")} testid="process-cta-mobile">{c.processCta}</ArrowCta>
                <p className="text-xs text-fog" data-testid="process-note">{c.processNote}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR UNDERWRITING ─────────────────────────────── */}
      <section className="relative bg-verde-950 text-cream grain overflow-hidden">
        <div className="container-x relative py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
            <div className="lg:col-span-4">
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-cream/80">
                <span className="h-[2px] w-9 rounded bg-cream/80" aria-hidden="true" />
                {c.uwEyebrow}
              </p>
              <h2 className="display mt-6 text-4xl sm:text-5xl text-white max-w-[330px]" data-testid="underwriting-headline">
                <MaskLineView>{c.uwHeadline1}</MaskLineView>
                <MaskLineView delay={0.1}>{c.uwHeadline2}</MaskLineView>
              </h2>
              <Reveal delay={0.2} className="mt-6 max-w-sm">
                <p className="text-[15px] text-cream/70 leading-relaxed" data-testid="underwriting-copy">
                  {c.uwCopy}
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-8 flex flex-col items-stretch gap-3 sm:grid sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-start sm:gap-4">
              {c.uwSteps.map((s, i) => (
                <div key={s.t} className="contents">
                  <Reveal delay={0.1 + i * 0.1}>
                    <div
                      className="relative h-full rounded-2xl border border-cream/15 bg-white/[0.04] px-5 pb-7 pt-12 mt-8 text-center transition-colors duration-300 hover:border-mint/30"
                      data-testid={`uw-step-${i}`}
                    >
                      <span className="group absolute -top-8 left-1/2 flex h-16 w-16 -translate-x-1/2 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#1C5241] via-[#144334] to-[#0E3527] ring-1 ring-mint/30 shadow-[0_0_34px_-8px_rgba(46,158,107,0.55)]">
                        <span className="absolute inset-0 bg-gradient-to-b from-white/15 via-transparent to-transparent" aria-hidden="true" />
                        <s.icon className="relative h-6 w-6 text-mint" strokeWidth={1.5} aria-hidden="true" />
                        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" aria-hidden="true" />
                      </span>
                      <p className="font-mono text-[11px] text-cream/50">0{i + 1}</p>
                      <h3 className="mt-2 font-display text-xl font-bold text-white">{s.t}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-cream/70">{s.d}</p>
                    </div>
                  </Reveal>
                  {i < c.uwSteps.length - 1 && (
                    <div className="flex justify-center py-1 sm:mt-16 sm:h-8 sm:items-center" aria-hidden="true">
                      <ArrowRight className="h-6 w-6 rotate-90 text-mint/80 sm:rotate-0" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Reveal delay={0.15} className="mt-14">
            <div
              className="rounded-2xl border border-cream/10 bg-verde-900/70 p-6 sm:p-8"
              data-testid="expert-banner"
            >
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">
                <span className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#1C5241] via-[#144334] to-[#0E3527] ring-1 ring-mint/30 shadow-[0_0_34px_-8px_rgba(46,158,107,0.55)]">
                  <span className="absolute inset-0 bg-gradient-to-b from-white/15 via-transparent to-transparent" aria-hidden="true" />
                  <Users className="relative h-6 w-6 text-mint" aria-hidden="true" />
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" aria-hidden="true" />
                </span>
                <div className="flex-1">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-mint">{c.bannerEyebrow}</p>
                  <p className="mt-1.5 font-display text-2xl font-bold text-white">{c.bannerTitle}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-cream/70">
                    {c.bannerCopy}
                  </p>
                </div>
                <ArrowCta to={lp("/expert-network")} variant="light" testid="underwriting-experts-cta">
                  {c.bannerCta}
                </ArrowCta>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-3.5 border-t border-cream/10 pt-8 sm:grid-cols-3" data-testid="expert-cards">
                {c.bannerExperts.map((e, i) => (
                  <div
                    key={e.name}
                    className="flex items-center gap-4 rounded-xl bg-verde-950/50 p-3.5 transition-colors duration-300 hover:bg-verde-950/80"
                    data-testid={`expert-card-${i}`}
                  >
                    <img
                      src={e.img}
                      alt={`${e.name}, ${e.role}`}
                      className="h-20 w-20 shrink-0 rounded-lg object-cover sm:h-24 sm:w-24"
                      loading="lazy"
                      decoding="async"
                      data-testid={`expert-photo-${i}`}
                    />
                    <div className="min-w-0">
                      <p className="font-display text-[15px] font-bold text-white" data-testid={`expert-name-${i}`}>{e.name}</p>
                      <p
                        className="mt-1.5 flex items-start gap-1.5 text-xs font-semibold leading-snug text-mint"
                        data-testid={`expert-role-${i}`}
                      >
                        <img
                          src="/favicon-white.png"
                          alt=""
                          className="mt-px h-3.5 w-3.5 shrink-0"
                          loading="lazy"
                          decoding="async"
                          aria-hidden="true"
                        />
                        <span>{e.role}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOR BROKERS AND ADVISERS ─────────────────────── */}
      <section className="bg-cream border-t border-sand overflow-hidden" data-testid="broker-section">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[300px] lg:min-h-[620px]">
            <img
              src="/broker-comporta.jpg"
              alt="Comporta-style residence in a high-end condominium, Portugal"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="broker-image"
            />
          </div>
          <div className="flex flex-col justify-center px-6 py-16 sm:px-10 lg:px-14 lg:py-20">
            <Eyebrow>{c.brokerEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl lg:text-[42px] text-charcoal max-w-xl" data-testid="broker-headline">
              <MaskLineView>{c.brokerHeadline1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.brokerHeadline2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-lg">
              <p className="text-[15px] leading-relaxed text-fog" data-testid="broker-copy">
                {c.brokerCopy}
              </p>
            </Reveal>

            <ol className="relative mt-10 sm:hidden" data-testid="broker-mobile-steps">
              {c.brokerSteps.map((s, i) => (
                <li key={s.t} className="relative flex gap-3.5 pb-4 last:pb-0" data-testid={`broker-mstep-${i}`}>
                  {i < c.brokerSteps.length - 1 && (
                    <span className="absolute left-[19px] top-10 h-[calc(100%-2.5rem)] w-px bg-sand" aria-hidden="true" />
                  )}
                  <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-verde-800 font-display text-[13px] font-bold text-white shadow-md shadow-verde-950/15">
                    {i + 1}
                  </span>
                  <Reveal delay={0.05 + i * 0.06} className="min-w-0 flex-1">
                    <div className="flex h-full items-center gap-3 rounded-2xl bg-white p-3.5 shadow-[0_16px_36px_-26px_rgba(7,39,29,0.35)]">
                      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10">
                        <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                        <s.icon className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-display text-[15px] font-bold leading-tight text-charcoal">{s.t}</p>
                        <p className="mt-0.5 text-[12px] leading-snug text-fog">{s.c}</p>
                      </div>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ol>

            <div className="mt-10 hidden gap-3 sm:grid sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-start" data-testid="broker-steps">
              {c.brokerSteps.map((s, i) => (
                <div key={s.t} className="contents">
                  <div data-testid={`broker-step-${i}`}>
                    <span className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_14px_30px_-16px_rgba(7,39,29,0.45)]">
                      <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                      <s.icon className="relative h-6 w-6 text-verde-800" strokeWidth={1.6} aria-hidden="true" />
                    </span>
                    <p className="mt-4 font-display text-lg font-bold text-charcoal">
                      {i + 1}. {s.t}
                    </p>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-fog sm:max-w-[190px]">{s.c}</p>
                  </div>
                  {i < c.brokerSteps.length - 1 && (
                    <div className="flex justify-center sm:mt-2 sm:h-16 sm:items-center" aria-hidden="true">
                      <ArrowRight className="h-5 w-5 rotate-90 text-verde-700/50 sm:rotate-0" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-3 sm:hidden" data-testid="broker-mobile-benefits">
              {c.brokerBenefits.map((b, i) => (
                <Reveal key={b.t} delay={0.05 + i * 0.06}>
                  <div className="flex items-center gap-3.5 rounded-2xl bg-white p-4 shadow-[0_16px_36px_-26px_rgba(7,39,29,0.35)]" data-testid={`broker-mbenefit-${i}`}>
                    <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10">
                      <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                      <b.icon className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-display text-[15px] font-bold leading-tight text-charcoal">{b.t}</p>
                      <p className="mt-0.5 text-[12px] leading-snug text-fog">{b.c}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 hidden gap-8 border-t border-sand pt-10 sm:grid sm:grid-cols-3 sm:gap-6" data-testid="broker-benefits">
              {c.brokerBenefits.map((b, i) => (
                <Reveal key={b.t} delay={0.1 + i * 0.08}>
                  <div data-testid={`broker-benefit-${i}`}>
                    <b.icon className="h-6 w-6 text-verde-700" strokeWidth={1.6} aria-hidden="true" />
                    <p className="mt-3.5 font-display text-base font-bold text-charcoal">{b.t}</p>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-fog">{b.c}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.3} className="mt-12">
              <ArrowCta to={lp("/brokers")} testid="broker-partner-cta">{c.brokerCta}</ArrowCta>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
